import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Cow, MapPin, CheckCircle, IdentificationCard } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import RECEmitidosTab from "@/components/rec/RECEmitidosTab";
import PuntosEmisionTab from "@/components/rec/PuntosEmisionTab";
import ActividadAFIPTab from "@/components/rec/ActividadAFIPTab";

export default function RECPage() {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get("/rec/status").then(({ data }) => setStatus(data)).catch(() => {});
    api.get("/rec/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const refreshCatalogs = async () => {
    try {
      await api.post("/rec/catalog/refresh");
      toast.success("Catálogos SENASA refrescados");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo refrescar");
    }
  };

  return (
    <div className="space-y-6 p-4" data-testid="rec-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <Cow size={28} weight="duotone" color="#921A0E" />
            Remitos Electrónicos Cárnicos
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Emisión de REC bovinos y porcinos según RG 5259 · SENASA + AFIP
          </p>
        </div>
        <Button
          variant="outline"
          onClick={refreshCatalogs}
          data-testid="rec-refresh-catalogs"
          className="text-xs"
        >
          Refrescar catálogos SENASA
        </Button>
      </div>

      {/* Cards de estado */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-widest text-neutral-500">
              Estado WSRemCarne
            </CardTitle>
          </CardHeader>
          <CardContent>
            {status?.ready ? (
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                <CheckCircle size={14} className="mr-1" /> Autorizado
              </Badge>
            ) : (
              <Badge variant="destructive">No autorizado</Badge>
            )}
            {status?.razon && (
              <div className="text-xs text-neutral-500 mt-2">{status.razon}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-widest text-neutral-500">
              RUCA emisor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-heading font-bold" data-testid="rec-ruca">
              {config?.ruca || "-"}
            </div>
            <div className="text-xs text-neutral-500 mt-1">{config?.razon_social}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-widest text-neutral-500">
              CUIT emisor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-heading font-bold">
              {config?.cuit_emisor || "-"}
            </div>
            <div className="text-xs text-neutral-500 mt-1">
              Pto emisión REC: {config?.punto_emision ?? "-"}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-widest text-neutral-500">
              Especies habilitadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 flex-wrap">
              {config?.especies && Object.entries(config.especies).map(([k, v]) => (
                <Badge key={k} variant="secondary" className="text-[10px] uppercase">
                  {v.label}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="recs" className="w-full">
        <TabsList data-testid="rec-tabs">
          <TabsTrigger value="recs" data-testid="tab-recs">
            <Cow size={16} className="mr-2" /> REC Emitidos
          </TabsTrigger>
          <TabsTrigger value="puntos-emision" data-testid="tab-puntos-emision">
            <MapPin size={16} className="mr-2" /> Puntos de emisión
          </TabsTrigger>
          <TabsTrigger value="actividad-afip" data-testid="tab-actividad-afip">
            <IdentificationCard size={16} className="mr-2" /> Actividad AFIP
          </TabsTrigger>
        </TabsList>
        <TabsContent value="recs" className="pt-4">
          <RECEmitidosTab config={config} />
        </TabsContent>
        <TabsContent value="puntos-emision" className="pt-4">
          <PuntosEmisionTab />
        </TabsContent>
        <TabsContent value="actividad-afip" className="pt-4">
          <ActividadAFIPTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
